import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Clock, Database, QrCode } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/hooks/ui/use-toast';
import { DotyposImportDialog } from '@/components/employees/DotyposImportDialog';
import TimeTrackingEnableWizard from '@/components/payroll/TimeTrackingEnableWizard';
import { SettingsCard } from '../ui/SettingsCard';

const TimeTrackingSystemSettings: React.FC = () => {
  const {
    timeTrackingEnabled,
    setTimeTrackingEnabled,
    timeTrackingDataSource,
    setTimeTrackingDataSource,
    business
  } = useAuth();

  const [showEnableWizard, setShowEnableWizard] = useState(false);
  const [showDotyposWizard, setShowDotyposWizard] = useState(false);
  const [pendingDataSource, setPendingDataSource] = useState<'dotypos' | 'hybrid'>('dotypos');

  const businessId = business?.id || null;

  const dataSourceLabels = {
    internal: 'Internal (QR/Manual)',
    dotypos: 'Dotypos POS',
    hybrid: 'Hybrid (Both Sources)'
  };

  const dataSourceDescriptions = {
    internal: 'Use QR code scanning and manual time entry',
    dotypos: 'Fetch attendance data from Dotypos POS system',
    hybrid: 'Combine both internal and Dotypos attendance data'
  };

  const handleEnableToggle = (checked: boolean) => {
    if (checked) {
      // Show the wizard dialog when enabling
      setShowEnableWizard(true);
    } else {
      // Disable immediately
      setTimeTrackingEnabled(false);
      toast.success("Time tracking disabled", {
        description: "Time tracking features are now disabled"
      });
    }
  };

  return (
    <>
      <SettingsCard
        icon={Clock}
        title="Time Tracking Settings"
        description="Configure time tracking and attendance features"
      >
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="text-sm font-medium">Enable Time Tracking</Label>
              <p className="text-sm text-muted-foreground">
                Turn on employee attendance tracking, QR code scanning, and time log management.
              </p>
            </div>
            <Switch
              checked={timeTrackingEnabled}
              onCheckedChange={handleEnableToggle}
            />
          </div>

          {timeTrackingEnabled && (
            <div className="pt-4 border-t space-y-3">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    Data Source
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Choose where to get attendance data from
                  </p>
                </div>
                <Select
                  value={timeTrackingDataSource}
                  onValueChange={(value: 'internal' | 'dotypos' | 'hybrid') => {
                    // If switching to dotypos or hybrid and wasn't before, show wizard
                    if ((value === 'dotypos' || value === 'hybrid') && timeTrackingDataSource === 'internal') {
                      setPendingDataSource(value);
                      setShowDotyposWizard(true);
                    } else {
                      setTimeTrackingDataSource(value);
                      toast.success(`Data source set to ${dataSourceLabels[value]}`, {
                        description: dataSourceDescriptions[value]
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select data source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">
                      <div className="flex items-center gap-2">
                        <QrCode className="h-4 w-4" />
                        Internal (QR/Manual)
                      </div>
                    </SelectItem>
                    <SelectItem value="dotypos">
                      <div className="flex items-center gap-2">
                        <img src="/media/dotypos.png" alt="Dotypos" className="h-4 w-4 invert dark:invert-0" />
                        Dotypos POS
                      </div>
                    </SelectItem>
                    <SelectItem value="hybrid">
                      <div className="flex items-center gap-2">
                        <Database className="h-4 w-4" />
                        Hybrid (Both)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {timeTrackingDataSource !== 'internal' && (
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">
                    <strong>Note:</strong> Dotypos integration requires your employees to be imported from Dotypos
                    with their Dotypos IDs mapped. Attendance data will be synced manually via the Time Tracking tab.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </SettingsCard>

      {/* Enable Time Tracking Wizard */}
      <TimeTrackingEnableWizard
        isOpen={showEnableWizard}
        onClose={() => setShowEnableWizard(false)}
        businessId={businessId}
        businessName={business?.name}
      />

      {/* Dotypos Employee Sync Wizard - for data source changes */}
      {businessId && (
        <DotyposImportDialog
          isOpen={showDotyposWizard}
          onClose={() => {
            setShowDotyposWizard(false);
            // User cancelled the wizard without completing
            toast("Employee sync cancelled", {
              description: "Data source change was not applied."
            });
          }}
          businessId={businessId}
          businessName={business?.name}
          onImportComplete={(summary) => {
            setShowDotyposWizard(false);

            // Now apply the data source change
            setTimeTrackingDataSource(pendingDataSource);

            const sourceLabels = {
              dotypos: 'Dotypos POS',
              hybrid: 'Hybrid (Both)'
            };

            toast.success(`Data source set to ${sourceLabels[pendingDataSource]}`, {
              description: `Employee sync complete: ${summary.created} created, ${summary.updated} updated, ${summary.skipped} skipped.`
            });
          }}
        />
      )}
    </>
  );
};

export default TimeTrackingSystemSettings;
